import React, { useEffect } from 'react';
import { Decorator, FORM_ERROR } from 'final-form';
import FinalForm, { Form as BaseFinalForm, FormSpyProps } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { ObjectSchema } from 'yup';
import { log } from '@economic/react-app';
import { useToast, ToastReference } from '@economic/taco';
import { AxiosError, AxiosResponse, isAxiosError } from 'axios';
import { getAPIErrorValidation, getErrorMessage } from '~/utilities/parseAPIValidationErrors';
import { convertErrors, blurFieldsBeforeSubmit } from './final-form-utils';
import { Calculation, createDecorator } from './final-form-calculate';
import createValidationHandler from './handlers/createValidationHandler';
import { BaseFormWithArrowKeyNavigation } from './BaseFormWithArrowKeyNavigation';

interface Props<FormValues = Record<string, never>, TResponse = never, InitialFormValues = Partial<FormValues>>
    extends Pick<FinalForm.FormProps<FormValues>, Exclude<keyof FinalForm.FormProps, 'initialValues' | 'onSubmit' | 'validate'>> {
    id?: string;
    className?: string;
    children: ((props: FinalForm.FormRenderProps<FormValues>) => React.ReactNode) | React.ReactNode;
    context?: object;
    /**
     * Always run updates for field when blurred, even if the value hasn't changed.
     */
    forceUpdatesOnBlur?: boolean;
    initialData?: InitialFormValues;
    horizontal?: boolean;
    messages?: {
        success?: string | React.ReactElement;
        loading?: string;
        error?: string;
    };
    onChange?: FormSpyProps<FormValues>['onChange'];
    onSubmitting?: (values: FormValues, FORM_ERROR: string) => void;
    onSubmit: (values: FormValues) => Promise<AxiosResponse<TResponse> | void> | void;
    onSubmitted?: (response: TResponse) => any;
    onSubmitError?: (error: AxiosError) => void;
    mapErrorMessages?: (errorCode: string, field: string, values?: FormValues) => string;
    skipNotifications?: boolean;
    updates?: ((context?: any) => Calculation[]) | Calculation[];
    validate?: (values: FormValues) => string | undefined;
    validationSchema?: ((values: FormValues) => ObjectSchema) | ObjectSchema;
    validateOnBlur?: boolean;
    onArrowKeySaveAndChange?: (nextItemId: string | number) => void;
    collectionData?: any[];
    idKeyProp?: string;
    currentItemId?: string | number;
}

const FormFieldContext = React.createContext<(field: string, label?: React.ReactNode) => void>(() => {});

export const useFormFieldRegistration = () => React.useContext(FormFieldContext);

const Form = <TValues extends object = Record<string, never>, TResponse = never>(props: Props<TValues, TResponse>) => {
    const {
        id,
        className,
        children,
        context,
        forceUpdatesOnBlur,
        initialData = {},
        horizontal = false,
        messages = {},
        mutators,
        onChange,
        onSubmit,
        onSubmitted,
        onSubmitting,
        onSubmitError,
        mapErrorMessages,
        skipNotifications = false,
        updates,
        validate,
        validationSchema,
        validateOnBlur = true,
        onArrowKeySaveAndChange,
        collectionData,
        idKeyProp,
        currentItemId,
        ...restProps
    } = props;
    const toast = useToast();
    const registeredFields = React.useRef({});
    const calculator = React.useMemo(() => {
        const calculations = typeof updates === 'function' ? updates(context) : updates;
        return calculations
            ? (createDecorator(calculations, { forceUpdatesOnBlur }) as unknown as Decorator<TValues>)
            : undefined;
    }, [updates, JSON.stringify(context), forceUpdatesOnBlur]);
    const decorators = React.useMemo(() => (calculator ? [calculator] : undefined), [calculator]);

    let toastRef: ToastReference | null = null;

    useEffect(() => {
        addEventListener('click', blurFieldsBeforeSubmit);
        return () => {
            removeEventListener('click', blurFieldsBeforeSubmit);
        };
    });

    const handleSubmit = async (values: TValues) => {
        if (!skipNotifications) {
            toastRef = toast.loading(messages.loading ?? '');
        }

        if (onSubmitting) {
            onSubmitting(values, FORM_ERROR);
        }

        try {
            const response = await onSubmit(values);

            if (!skipNotifications && toastRef) {
                toastRef.success(messages.success ?? '');
            }

            if (onSubmitted) {
                onSubmitted((response as AxiosResponse<TResponse>)?.data);
            }
        } catch (error) {
            if (isAxiosError(error) && error.response && error.response.status >= 500) {
                log.error(error);
            }

            error.validation = getAPIErrorValidation(error, values, mapErrorMessages);

            if (!skipNotifications) {
                const message = getErrorMessage(error, messages.error, registeredFields.current);

                toastRef?.error(message);
            }

            if (onSubmitError) {
                onSubmitError(error);
            }

            return convertErrors(error.validation);
        }
    };

    const validator = React.useMemo(
        () => createValidationHandler(validationSchema, validate, context),
        [JSON.stringify(context)]
    );

    const registerField = React.useCallback((name: string, label?: React.ReactNode) => {
        registeredFields.current[name] = label;
    }, []);

    return (
        <FormFieldContext.Provider value={registerField}>
            <BaseFinalForm
                {...restProps}
                decorators={decorators}
                initialValues={initialData}
                mutators={{ ...mutators, ...arrayMutators }}
                onSubmit={handleSubmit}
                validate={validator}
                validateOnBlur={validateOnBlur}>
                {renderProps => {
                    return (
                        <BaseFormWithArrowKeyNavigation
                            id={id}
                            collectionData={collectionData}
                            currentItemId={currentItemId}
                            idKeyProp={idKeyProp}
                            handleSubmit={handleSubmit}
                            validator={validator}
                            onArrowKeySaveAndChange={onArrowKeySaveAndChange}
                            children={children}
                            renderProps={renderProps}
                            className={className}
                            horizontal={horizontal}
                            onChange={onChange}
                        />
                    );
                }}
            </BaseFinalForm>
        </FormFieldContext.Provider>
    );
};

Form.ErrorMessage = (props: React.HTMLAttributes<HTMLDivElement>) => <div {...props} className="form__error" />;

export default Form;
